import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiError, browserApiClient, requiredApiData } from './browser-api';
import { verticalSliceQueryKey } from './use-vertical-slice';
import { tribeSocialQueryKey } from './tribe-query-keys';

export type TradeResource = 'maize' | 'stone' | 'timber';
export type MarketOrderState = 'active' | 'filled' | 'cancelled' | 'expired';

export interface TribeExchangeOrder {
  id: string;
  tribeId: string;
  sellerMemberId: string;
  sellerDisplayName: string;
  offeredResourceCode: TradeResource;
  offeredAmount: number;
  requestedResourceCode: TradeResource;
  requestedAmount: number;
  status: MarketOrderState;
  isOwn: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface TribeExchangeBoard {
  tribeId: string;
  activeOrderLimit: number;
  orders: readonly TribeExchangeOrder[];
}

export type TribeExchangeCommand =
  | {
      type: 'create';
      commandId: string;
      offeredResourceCode: TradeResource;
      offeredAmount: number;
      requestedResourceCode: TradeResource;
      requestedAmount: number;
    }
  | { type: 'fill'; commandId: string; orderId: string }
  | { type: 'cancel'; commandId: string; orderId: string };

function mapOrder(order: {
  id: string;
  tribe_id: string;
  seller_member_id: string;
  seller_display_name: string;
  offered_resource_code: TradeResource;
  offered_amount: number;
  requested_resource_code: TradeResource;
  requested_amount: number;
  status: MarketOrderState;
  is_own: boolean;
  created_at: string;
  expires_at: string;
}): TribeExchangeOrder {
  return {
    id: order.id,
    tribeId: order.tribe_id,
    sellerMemberId: order.seller_member_id,
    sellerDisplayName: order.seller_display_name,
    offeredResourceCode: order.offered_resource_code,
    offeredAmount: order.offered_amount,
    requestedResourceCode: order.requested_resource_code,
    requestedAmount: order.requested_amount,
    status: order.status,
    isOwn: order.is_own,
    createdAt: order.created_at,
    expiresAt: order.expires_at,
  };
}

export const tribeExchangeQueryKey = (tribeId: string) =>
  ['tribe', tribeId, 'exchange', 'v1'] as const;

export async function loadTribeExchange(
  tribeId: string,
  signal?: AbortSignal,
): Promise<TribeExchangeBoard> {
  const response = await browserApiClient().GET('/api/v1/tribes/{tribe_id}/exchange', {
    params: { path: { tribe_id: tribeId } },
    signal,
  });
  const data = requiredApiData(response.data, response.error, 'The Tribe Exchange is unavailable.');
  return {
    tribeId: data.tribe_id,
    activeOrderLimit: data.active_order_limit,
    orders: data.orders.map(mapOrder),
  };
}

export async function submitTribeExchangeCommand(
  tribeId: string,
  command: TribeExchangeCommand,
): Promise<TribeExchangeOrder> {
  const client = browserApiClient({ mutation: true });
  if (command.type === 'create') {
    const response = await client.POST('/api/v1/tribes/{tribe_id}/exchange/offers', {
      params: {
        path: { tribe_id: tribeId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: {
        offered_resource_code: command.offeredResourceCode,
        offered_amount: command.offeredAmount,
        requested_resource_code: command.requestedResourceCode,
        requested_amount: command.requestedAmount,
      },
    });
    const data = requiredApiData(response.data, response.error, 'The offer could not be placed.');
    return mapOrder(data.order);
  }
  const response =
    command.type === 'fill'
      ? await client.POST('/api/v1/tribes/{tribe_id}/exchange/offers/{order_id}/fill', {
          params: {
            path: { tribe_id: tribeId, order_id: command.orderId },
            header: { 'Idempotency-Key': command.commandId },
          },
        })
      : await client.POST('/api/v1/tribes/{tribe_id}/exchange/offers/{order_id}/cancel', {
          params: {
            path: { tribe_id: tribeId, order_id: command.orderId },
            header: { 'Idempotency-Key': command.commandId },
          },
        });
  if (!response.data) throw apiError(response.error, 'The offer could not be changed.');
  return mapOrder(response.data.order);
}

export function useTribeExchangeQuery(tribeId: string | undefined) {
  return useQuery({
    enabled: Boolean(tribeId),
    queryKey: tribeExchangeQueryKey(tribeId ?? 'none'),
    queryFn: ({ signal }) => loadTribeExchange(tribeId ?? '', signal),
    staleTime: 15_000,
  });
}

export function useTribeExchangeCommand(tribeId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: TribeExchangeCommand) => {
      if (!tribeId) throw new Error('Join a Tribe before using its Exchange.');
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Reconnect before changing an exchange offer.');
      }
      return submitTribeExchangeCommand(tribeId, command);
    },
    onSuccess: async () => {
      if (tribeId) {
        await queryClient.invalidateQueries({ queryKey: tribeExchangeQueryKey(tribeId) });
        await queryClient.invalidateQueries({
          queryKey: tribeSocialQueryKey(tribeId),
          exact: true,
        });
      }
      await queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey });
    },
  });
}
